import LegalLayout from './LegalLayout'
import { CheckCircle2, AlertTriangle, Clock, Plane, ShieldCheck } from 'lucide-react'

export default function CancellationPolicy() {
  return (
    <LegalLayout
      activeTab="cancellation"
      title="Cancellation & Refund Policy"
      subtitle="Clear, fair, and flexible cancellation terms for all Salzburg Cab private transfers, ski shuttles, and day tours."
      lastUpdated="January 2026"
    >
      <div className="space-y-8 font-host text-gray-700 leading-relaxed text-sm sm:text-[15px]">
        {/* Highlight Guarantee Box */}
        <div className="rounded-2xl border border-[#85B718]/40 bg-gradient-to-r from-[#85B718]/10 via-[#85B718]/5 to-transparent p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#85B718] text-white shadow-sm">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-gray-900">
                100% Free Cancellation Up to 24 Hours Before Pickup
              </h2>
              <p className="mt-1 text-xs sm:text-sm text-gray-600">
                Plans change, and we understand. Cancel any airport transfer, ski shuttle, or private tour at least 24 hours before your scheduled pickup time for a 100% full refund with no hidden penalty fees.
              </p>
            </div>
          </div>
        </div>

        {/* Section 1: Timeline Breakdown */}
        <section className="space-y-4">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900">
            1. Cancellation Timeline & Refund Schedule
          </h3>
          <p className="text-gray-600">
            Refund eligibility depends on when the cancellation request is received relative to your scheduled pickup time:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
            {/* 24+ Hours */}
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
              <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm">
                <CheckCircle2 className="h-4 w-4" />
                <span>24+ Hours Notice</span>
              </div>
              <p className="mt-2 text-2xl font-bold text-gray-900">100% Refund</p>
              <p className="mt-1.5 text-xs text-gray-600">
                Full refund to original payment method. No administrative cancellation fee.
              </p>
            </div>

            {/* 12 - 24 Hours */}
            <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
              <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm">
                <Clock className="h-4 w-4" />
                <span>12 to 24 Hours Notice</span>
              </div>
              <p className="mt-2 text-2xl font-bold text-gray-900">50% Refund</p>
              <p className="mt-1.5 text-xs text-gray-600">
                50% refund, or free reschedule to another available date/time upon request.
              </p>
            </div>

            {/* Under 12 Hours */}
            <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4">
              <div className="flex items-center gap-2 text-rose-700 font-semibold text-sm">
                <AlertTriangle className="h-4 w-4" />
                <span>Less than 12 Hours</span>
              </div>
              <p className="mt-2 text-2xl font-bold text-gray-900">Non-Refundable</p>
              <p className="mt-1.5 text-xs text-gray-600">
                Chauffeur and premium vehicle are reserved and already in dispatch preparation.
              </p>
            </div>
          </div>
        </section>

        {/* Section 2: Flight Delays & Airport Guarantee */}
        <section className="space-y-3">
          <div className="flex items-center gap-2.5">
            <Plane className="h-5 w-5 text-[#FF7027]" />
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">
              2. Flight Delay Protection (Airport Pickups)
            </h3>
          </div>
          <p className="text-gray-600">
            If your flight is delayed or rescheduled by the airline, you do <strong className="text-gray-900">not</strong> need to cancel your booking.
          </p>
          <div className="rounded-xl bg-gray-50 border border-gray-200/70 p-4 text-xs sm:text-sm text-gray-700 space-y-2">
            <p>
              &bull; <strong>Automated Flight Tracking:</strong> We monitor all incoming flight numbers. Your driver will adjust their arrival time to match your actual landing time at no extra charge.
            </p>
            <p>
              &bull; <strong>Cancelled Flights:</strong> If your airline cancels your flight and you notify us before the driver departs for the airport, you are entitled to a full 100% refund or free voucher credit.
            </p>
          </div>
        </section>

        {/* Section 3: Ski Transfers & Mountain Weather */}
        <section className="space-y-3">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900">
            3. Ski Transfers & Alpine Weather Exceptions
          </h3>
          <p className="text-gray-600">
            In winter Alpine regions (Zell am See, Kitzbühel, Saalbach, Flachau, Bad Gastein, etc.), extreme weather may occur. If official Austrian road authorities close access roads due to avalanche hazards or severe snowstorms, Salzburg Cab provides:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-gray-600">
            <li>Immediate notification and recommendation of safe alternative routes where possible.</li>
            <li>If safe passage is physically impossible, a 100% full refund with zero cancellation charges.</li>
          </ul>
        </section>

        {/* Section 4: How to Cancel */}
        <section className="space-y-3">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900">
            4. How to Request a Cancellation or Modification
          </h3>
          <p className="text-gray-600">
            You can request a cancellation or change through any of the following fast channels:
          </p>
          <ul className="list-disc pl-5 space-y-2 text-gray-600">
            <li>
              <strong className="text-gray-900">Online Account:</strong> Log in to your Salzburg Cab account, navigate to &ldquo;My Trips&rdquo;, and click &ldquo;Cancel Trip&rdquo;.
            </li>
            <li>
              <strong className="text-gray-900">Email:</strong> Reply directly to your booking confirmation email or send an email to{' '}
              <a href="mailto:office@salzburgcab.com" className="text-[#FF7027] font-semibold hover:underline">
                office@salzburgcab.com
              </a>{' '}
              with your booking reference number.
            </li>
            <li>
              <strong className="text-gray-900">Telephone / WhatsApp:</strong> Call our 24/7 hotline at{' '}
              <a href="tel:+43720775053" className="text-[#FF7027] font-semibold hover:underline">
                +43 720 7750 53
              </a>{' '}
              for immediate assistance.
            </li>
          </ul>
        </section>

        {/* Section 5: Refund Processing Timelines */}
        <section className="space-y-3">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900">
            5. Refund Processing Timelines
          </h3>
          <p className="text-gray-600">
            Once a cancellation is confirmed, refunds are authorized immediately by our billing department. Funds typically appear back in your original account or card within <strong className="text-gray-900">3 to 5 business days</strong>, depending on your financial institution&rsquo;s processing schedule.
          </p>
        </section>
      </div>
    </LegalLayout>
  )
}
